/**
 * IM-004 / IM-005: PDP stock row uses warehouse `inventoryIds` from cookie.
 * Refetches the active SKU via Shopper Products (same as Notify Me / ATC block) because
 * master `c_allVariantsData` and embedded variant ATS are default-list only.
 */

import {useMemo} from 'react';

import {usePdpShopperInventoryIds} from '@amblique/king-living/app/hooks/use-pdp-shopper-inventory-ids';
import {useProducts} from '@amblique/king-living/app/hooks/use-products';
import {GET_PRODUCTS_PARAMS} from '@amblique/king-living/app/utils/api-utils';
import {getPdpStockIndicator, STOCK_INDICATOR} from '@amblique/king-living/app/utils/pdp-stock-utils';

/**
 * SCAPI product id for the SKU whose warehouse inventory should drive the stock row.
 *
 * @param {Object|null|undefined} product
 * @param {Object|null|undefined} variant
 * @returns {string|null}
 */
export function resolvePdpStockIndicatorProductId(product, variant) {
  if (!product && !variant) {
    return null;
  }

  return (
    variant?.productId ??
    variant?.id ??
    product?.id ??
    product?.productId ??
    null
  );
}

/**
 * @param {Object} options
 * @param {Object|null|undefined} options.product - PDP master (or variation) product
 * @param {Object|null|undefined} options.variant - Selected variant / cover entry
 * @param {string|undefined|null} options.shopperInventoryIds - IM-004 cookie `inventoryIds`
 * @returns {{ row: string, isPending: boolean }}
 */
export function usePdpStockIndicator({product, variant, shopperInventoryIds: shopperInventoryIdsProp}) {
  const {shopperInventoryIds: pdpInventoryIds, isInventoryIdsResolving} = usePdpShopperInventoryIds();
  const shopperInventoryIds = shopperInventoryIdsProp ?? pdpInventoryIds;

  const productId = useMemo(
    () => resolvePdpStockIndicatorProductId(product, variant),
    [product, variant]
  );

  const needsWarehouseFetch = Boolean(shopperInventoryIds && productId);

  const {data, isLoading, isFetching} = useProducts(
    {
      parameters: {
        ...GET_PRODUCTS_PARAMS,
        ids: productId,
      },
    },
    {
      enabled: needsWarehouseFetch && Boolean(product) && !isInventoryIdsResolving,
      staleTime: 0,
    },
    {inventoryIds: shopperInventoryIds}
  );

  const warehouseProduct = data?.data?.[0];
  const isPending =
    isInventoryIdsResolving || (needsWarehouseFetch && (isLoading || isFetching));

  const row = useMemo(() => {
    if (!product) {
      return STOCK_INDICATOR.NONE;
    }

    if (needsWarehouseFetch) {
      if (isPending) {
        return STOCK_INDICATOR.NONE;
      }

      if (warehouseProduct) {
        return getPdpStockIndicator(warehouseProduct, null, shopperInventoryIds);
      }
    }

    return getPdpStockIndicator(product, variant, shopperInventoryIds);
  }, [
    product,
    variant,
    shopperInventoryIds,
    isInventoryIdsResolving,
    needsWarehouseFetch,
    isPending,
    warehouseProduct,
  ]);

  return {isPending, row};
}

export default usePdpStockIndicator;
